import { useTheme } from "@emotion/react";
import React, { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import { CampaignContext } from "../../context/CampaignContext";
import { customersEnum } from "../constants/Enums";
import { getCampaignsOfTheYear } from "../requests/Requests";
import styles from "../../assets/css/sideMenu.module.css";

const TREE_INDENT = 18;
const YEAR_CONTENT_INDENT = 14;
const TREE_LINE_COLOR = "rgba(19, 63, 38, 0.25)";
const ROOT_LINE_LEFT = 7;
const FOLDER_ICON_LINE_OFFSET = 31;
const TREE_ELBOW_WIDTH = 18;

const getFolderLineLeft = (depth) => {
    const parentDepth = depth - 1;
    const parentPaddingLeft = parentDepth * TREE_INDENT + (parentDepth > 0 ? 24 : 0);
    return parentPaddingLeft + FOLDER_ICON_LINE_OFFSET;
};

function TreeConnector({ depth, isLast = false, forceRoot = false }) {
    if (depth === 0 && !forceRoot) return null;
    const lineLeft = forceRoot ? ROOT_LINE_LEFT : getFolderLineLeft(depth);
    return (
        <span
            aria-hidden="true"
            style={{
                position: "absolute",
                left: `${lineLeft}px`,
                top: 0,
                bottom: isLast ? "50%" : 0,
                width: "1px",
                background: TREE_LINE_COLOR,
            }}
        />
    );
}

function TreeElbow({ depth, forceRoot = false }) {
    if (depth === 0 && !forceRoot) return null;
    const lineLeft = forceRoot ? ROOT_LINE_LEFT : getFolderLineLeft(depth);
    return (
        <span
            aria-hidden="true"
            style={{
                position: "absolute",
                left: `${lineLeft}px`,
                top: "50%",
                width: `${TREE_ELBOW_WIDTH}px`,
                height: "1px",
                background: TREE_LINE_COLOR,
            }}
        />
    );
}

export default function AccordionItem({ title }) {
    const {
        selectedCampaign: selectedCampaignBase,
        campaignsToYearMap: campaignsToYearMapBase,
    } = useContext(CampaignContext);

    const [selectedCampaign, setSelectedCampaign] = selectedCampaignBase;
    const [campaignsToYearMap, setCampaignsToYearMap] = campaignsToYearMapBase;
    const [isActive, setIsActive] = useState(false);
    const [openFolders, setOpenFolders] = useState(new Set());
    const theme = useTheme();
    const navigate = useNavigate();

    React.useEffect(() => {
        const runEffect = async () => {
            if (selectedCampaign && selectedCampaign.year === title) {
                setIsActive(true);
                await fetchCampaigns(title);
            }
        };
        runEffect();
    }, [selectedCampaign]);

    const fetchCampaigns = async (year) => {
        const existingCampaigns = campaignsToYearMap.get(year);
        if (existingCampaigns && existingCampaigns.length > 0) return;
        const campaigns = await getCampaignsOfTheYear(year);
        const stateClone = new Map(campaignsToYearMap);
        stateClone.set(year, campaigns);
        setCampaignsToYearMap(stateClone);
    };

    const handleYearClick = async () => {
        const nextIsActive = !isActive;
        setIsActive(nextIsActive);
        if (nextIsActive) {
            await fetchCampaigns(title);
        }
    };

    const handleView = (campaign) => {
        setSelectedCampaign(campaign);
        navigate("/campaign/" + campaign.id);
    };

    const renderCampaignDisplayName = (campaign) => {
        const customers = customersEnum
            .filter((customer) => campaign.customerIds.includes(customer.id))
            .map((customer) => customer.displayName);
        const customersStr = customers.join("/");
        return `${campaign.jobNumber} ${customersStr} ${campaign.jobTitle} uge ${campaign.week}`;
    };

    const getVirtualFolder = (campaign) => {
        return campaign.virtualFolder?.trim() || null;
    };

    const campaigns = campaignsToYearMap.get(title) ?? [];

    const folderTree = useMemo(() => {
        const root = {
            name: "root",
            folders: new Map(),
            campaigns: [],
        };

        const addedCampaignIds = new Set();

        campaigns.forEach((campaign) => {
            if (!campaign || addedCampaignIds.has(campaign.id)) return;
            addedCampaignIds.add(campaign.id);

            const virtualFolder = getVirtualFolder(campaign);

            if (!virtualFolder) {
                root.campaigns.push(campaign);
                return;
            }

            if (!root.folders.has(virtualFolder)) {
                root.folders.set(virtualFolder, {
                    name: virtualFolder,
                    folders: new Map(), // always empty — no nesting
                    campaigns: [],
                });
            }

            root.folders.get(virtualFolder).campaigns.push(campaign);
        });

        return root;
    }, [campaigns]);

    const toggleFolder = (folderKey) => {
        setOpenFolders((previous) => {
            const next = new Set(previous);
            if (next.has(folderKey)) {
                next.delete(folderKey);
            } else {
                next.add(folderKey);
            }
            return next;
        });
    };

    const renderCampaign = (campaign, depth = 0, isLast = false, showRootConnector = false) => {
        const isSelected = selectedCampaign && selectedCampaign.id === campaign.id;
        const shouldShowConnector = depth > 0 || showRootConnector;
        return (
            <div
                key={campaign.id}
                className={styles.campaignItem}
                onClick={() => handleView(campaign)}
                style={{
                    position: "relative",
                    minHeight: "30px",
                    padding: "5px 0",
                    paddingLeft: `${depth * TREE_INDENT + 42}px`,
                    cursor: "pointer",
                    color: isSelected ? "#ff8862" : "#133f26",
                }}
            >
                {shouldShowConnector && (
                    <>
                        <TreeConnector depth={depth} isLast={isLast} forceRoot={showRootConnector} />
                        <TreeElbow depth={depth} forceRoot={showRootConnector} />
                    </>
                )}
                {renderCampaignDisplayName(campaign)}
            </div>
        );
    };

    const renderFolderNode = (node, parentKey = "", depth = 0) => {
        const folders = Array.from(node.folders.values()).sort((a, b) =>
            a.name.localeCompare(b.name)
        );
        const directCampaigns = node.campaigns
            .slice()
            .sort((a, b) =>
                renderCampaignDisplayName(a).localeCompare(renderCampaignDisplayName(b))
            );

        const hasFolders = folders.length > 0;
        const hasDirectCampaigns = directCampaigns.length > 0;

        return (
            <>
                {folders.map((folder, index) => {
                    const isLastFolder = index === folders.length - 1;
                    const folderKey = parentKey ? `${parentKey}\\${folder.name}` : folder.name;
                    const isOpen = openFolders.has(folderKey);

                    return (
                        <div key={folderKey}>
                            <div
                                className={styles.folderItem}
                                onClick={() => toggleFolder(folderKey)}
                                style={{
                                    position: "relative",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "7px",
                                    minHeight: "22px",
                                    padding: "2px 0",
                                    paddingLeft: `${depth * TREE_INDENT + (depth > 0 ? 24 : 0)}px`,
                                    cursor: "pointer",
                                    color: "#133f26",
                                }}
                            >
                                <TreeConnector
                                    depth={depth}
                                    isLast={isLastFolder && !isOpen && !hasDirectCampaigns}
                                />
                                <TreeElbow depth={depth} />
                                <span style={{ width: "14px", display: "inline-block", flexShrink: 0 }}>
                                    {isOpen ? "▾" : "▸"}
                                </span>
                                <FolderOpenIcon style={{ fontSize: 20, color: "#133f26", flexShrink: 0 }} />
                                <span>{folder.name}</span>
                            </div>
                            {isOpen && (
                                <div className={styles.folderChildren} style={{ position: "relative" }}>
                                    {renderFolderNode(folder, folderKey, depth + 1)}
                                </div>
                            )}
                        </div>
                    );
                })}

                {depth === 0 && hasFolders && hasDirectCampaigns && (
                    <div
                        aria-hidden="true"
                        style={{
                            height: "1px",
                            background: "rgba(19, 63, 38, 0.18)",
                            margin: "10px 0 8px 0",
                            width: "88%",
                        }}
                    />
                )}

                {directCampaigns.map((campaign, index) => {
                    const isRootCampaign = depth === 0;
                    const isLastCampaign = index === directCampaigns.length - 1;
                    return renderCampaign(campaign, depth, isLastCampaign, isRootCampaign);
                })}
            </>
        );
    };

    return (
        <div className={styles.body}>
            <div style={{ color: theme.palette.text.primary, cursor: "pointer" }}>
                <div style={{ fontFamily: "HillBold", display: "flex", alignItems: "center" }}>
                    <div onClick={handleYearClick} style={{ display: "flex", alignItems: "center" }}>
                        <span style={{ width: "12px", display: "inline-block", flexShrink: 0 }}>
                            {isActive ? "▾" : "▸"}
                        </span>
                        <span>{title}</span>
                    </div>
                    {isActive && (
                        <button
                            type="button"
                            aria-label={`Opret mappe for ${title}`}
                            title="Opret mappe"
                            onClick={(event) => {
                                event.stopPropagation();
                                console.log("Create folder for year:", title);
                            }}
                            style={{
                                background: "transparent",
                                border: "none",
                                color: "#133f26",
                                cursor: "pointer",
                                padding: 0,
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                fontFamily: "HillBold",
                                fontSize: "12px",
                                whiteSpace: "nowrap",
                            }}
                        >
                        </button>
                    )}
                </div>
            </div>
            {isActive && (
                <div className={styles.bodyItem} style={{ paddingLeft: `${YEAR_CONTENT_INDENT}px` }}>
                    {renderFolderNode(folderTree)}
                </div>
            )}
        </div>
    );
}